import { trackingPercentToEm } from './typography-tracking';

type ModeAwareCssValue = {
  base: string;
  _torch: string;
};

type TypographyMetric = {
  fontFamily: 'sans' | 'mono';
  fontSize: ModeAwareCssValue;
  lineHeight: ModeAwareCssValue;
  fontWeight: 'medium' | 'normal';
  letterSpacing: ModeAwareCssValue;
};

const size = ({
  platform,
  torch,
}: {
  platform: string;
  torch: string;
}): ModeAwareCssValue => ({
  base: platform,
  _torch: torch,
});

export const typographyMetrics = {
  headingHero: {
    fontFamily: 'sans',
    fontSize: size({ platform: '2xl', torch: 'lg' }),
    lineHeight: size({ platform: '2xl', torch: 'lg' }),
    fontWeight: 'medium',
    letterSpacing: size({
      platform: trackingPercentToEm(-0.6),
      torch: trackingPercentToEm(-0.5),
    }),
  },
  headingPage: {
    fontFamily: 'sans',
    fontSize: size({ platform: 'xl', torch: '15' }),
    lineHeight: size({ platform: 'xl', torch: 'md' }),
    fontWeight: 'medium',
    letterSpacing: size({
      platform: trackingPercentToEm(-0.5),
      torch: trackingPercentToEm(-0.3),
    }),
  },
  headingModal: {
    fontFamily: 'sans',
    fontSize: size({ platform: 'lg', torch: '15' }),
    lineHeight: size({ platform: 'lg', torch: 'md' }),
    fontWeight: 'medium',
    letterSpacing: size({
      platform: trackingPercentToEm(-0.4),
      torch: trackingPercentToEm(-0.3),
    }),
  },
  headingSection: {
    fontFamily: 'sans',
    fontSize: size({ platform: 'md', torch: '13' }),
    lineHeight: size({ platform: 'lg', torch: 'sm' }),
    fontWeight: 'medium',
    letterSpacing: size({
      platform: trackingPercentToEm(-0.3),
      torch: trackingPercentToEm(-0.25),
    }),
  },
  headingItem: {
    fontFamily: 'sans',
    fontSize: size({ platform: 'sm', torch: '13' }),
    lineHeight: size({ platform: 'sm', torch: 'xs' }),
    fontWeight: 'medium',
    letterSpacing: size({
      platform: trackingPercentToEm(-0.2),
      torch: trackingPercentToEm(-0.15),
    }),
  },
  bodyLarge: {
    fontFamily: 'sans',
    fontSize: size({ platform: 'md', torch: '13' }),
    lineHeight: size({ platform: 'lg', torch: 'sm' }),
    fontWeight: 'normal',
    letterSpacing: size({
      platform: trackingPercentToEm(-0.3),
      torch: trackingPercentToEm(-0.25),
    }),
  },
  bodyDefault: {
    fontFamily: 'sans',
    fontSize: size({ platform: 'sm', torch: 'xs' }),
    lineHeight: size({ platform: 'sm', torch: 'xs' }),
    fontWeight: 'normal',
    letterSpacing: size({
      platform: trackingPercentToEm(-0.2),
      torch: trackingPercentToEm(-0.15),
    }),
  },
  bodyStrong: {
    fontFamily: 'sans',
    fontSize: size({ platform: 'sm', torch: 'xs' }),
    lineHeight: size({ platform: 'sm', torch: 'xs' }),
    fontWeight: 'medium',
    letterSpacing: size({
      platform: trackingPercentToEm(-0.2),
      torch: trackingPercentToEm(-0.15),
    }),
  },
  labelDefault: {
    fontFamily: 'sans',
    fontSize: size({ platform: 'xs', torch: '11' }),
    lineHeight: size({ platform: '2xs', torch: '2xs' }),
    fontWeight: 'medium',
    letterSpacing: size({
      platform: trackingPercentToEm(-0.1),
      torch: trackingPercentToEm(-0.05),
    }),
  },
  metaDefault: {
    fontFamily: 'sans',
    fontSize: size({ platform: 'xs', torch: '11' }),
    lineHeight: size({ platform: '2xs', torch: '2xs' }),
    fontWeight: 'normal',
    letterSpacing: size({
      platform: trackingPercentToEm(-0.1),
      torch: trackingPercentToEm(-0.05),
    }),
  },
  monoDefault: {
    fontFamily: 'mono',
    fontSize: size({ platform: '13', torch: 'xs' }),
    lineHeight: size({ platform: 'sm', torch: 'xs' }),
    fontWeight: 'normal',
    letterSpacing: size({ platform: '0em', torch: '0em' }),
  },
  monoSmall: {
    fontFamily: 'mono',
    fontSize: size({ platform: 'xs', torch: '11' }),
    lineHeight: size({ platform: '2xs', torch: '2xs' }),
    fontWeight: 'normal',
    letterSpacing: size({ platform: '0em', torch: '0em' }),
  },
} as const satisfies Record<string, TypographyMetric>;

export type TypographyVariant = keyof typeof typographyMetrics;

export const typographyClassNames = {
  headingHero: 'terra-typography-heading-hero',
  headingPage: 'terra-typography-heading-page',
  headingModal: 'terra-typography-heading-modal',
  headingSection: 'terra-typography-heading-section',
  headingItem: 'terra-typography-heading-item',
  bodyLarge: 'terra-typography-body-large',
  bodyDefault: 'terra-typography-body-default',
  bodyStrong: 'terra-typography-body-strong',
  labelDefault: 'terra-typography-label-default',
  metaDefault: 'terra-typography-meta-default',
  monoDefault: 'terra-typography-mono-default',
  monoSmall: 'terra-typography-mono-small',
} as const satisfies Record<TypographyVariant, string>;
